import React, { useState, useMemo, useEffect, Suspense, lazy, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { TrendingUp, BarChart, RefreshCw, Settings, Calculator, AlertTriangle, BookOpen } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LandingPage } from './components/LandingPage';
import { DatabaseSelection } from './components/DatabaseSelection';
import { DatabaseManagement } from './components/DatabaseManagement';
import { KPIDashboard } from './components/KPIDashboard';
import { DataTable } from './components/DataTable';
import { AnalyticsDashboard } from './components/AnalyticsCharts';
import { GapInsights } from './components/GapInsights';
import { FilterBar } from './components/FilterBar';
import { TickerDetail } from './components/TickerDetail';
import { SetupOccurrencesDetail } from './components/SetupOccurrencesDetail';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserMenu } from './components/UserMenu';
import { DashboardLayout } from './components/DashboardLayout';
import { useAuth } from './hooks/useAuth';
import { StockData, CalculatedMetrics, FilterState } from './types';
import { calculateMetrics, calculateKPIs } from './utils/calculations';
import { PerformanceMonitor } from './components/PerformanceMonitor';
import { useConnectionStatus } from './hooks/useConnectionStatus';
import { useNetworkResilience } from './hooks/useNetworkResilience';
import { NetworkStatusIndicator } from './components/NetworkStatusIndicator';
import { GlobalLoadingOverlay } from './components/GlobalLoadingOverlay';
import { trackRenderTime, trackComponentLifecycle } from './utils/performance';
import { clearDatabaseCache, loadDatabaseWithRetry } from './utils/optimizedDatabaseManager';

// Lazy load components that aren't needed immediately
const Backtesting = lazy(() => import('./components/Backtesting').then(module => ({ default: module.Backtesting })));
const GuidedAnalysis = lazy(() => import('./components/GuidedAnalysis').then(module => ({ default: module.GuidedAnalysis })));
const DilutionAnalysis = lazy(() => import('./components/analytics/DilutionAnalysis'));
const TradingJournal = lazy(() => import('./components/journal/TradingJournal').then(module => ({ default: module.TradingJournal })));

interface DashboardProps {
  rawData: StockData[];
  currentDatabaseName: string;
  onDatabaseSelected: (data: StockData[], databaseName: string) => void;
  onNewFile: () => void;
  calculatedData: CalculatedMetrics[];
  successMessage: string;
  errorMessage: string;
  onClearMessages: () => void;
}

function Dashboard({ 
  rawData, 
  currentDatabaseName,
  onDatabaseSelected,
  onNewFile,
  calculatedData,
  successMessage,
  errorMessage,
  onClearMessages
}: DashboardProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'gaps' | 'analytics'>('gaps');
  const [filters, setFilters] = useState<FilterState>({
    dateRange: [null, null],
    openPriceRange: [0, Infinity],
    gapPercentRange: [0, Infinity],
    pmVolumeRange: [0, Infinity],
    openToPmhRange: [-Infinity, Infinity],
    fiveMinLowRange: [-Infinity, Infinity],
    thirtyMinLowRange: [-Infinity, Infinity],
    hodTimeRange: ['00:00', '23:59'],
    closeStatus: 'all',
    searchTicker: '',
    marketCapRange: [0, Infinity],
    sharesOutstandingRange: [0, Infinity],
    pmhBreak: 'all',
    hodTimeFilter: 'all'
  });

  // Track component render time
  const finishRenderTracking = trackRenderTime('Dashboard');
  
  // Track component lifecycle
  useEffect(() => {
    return trackComponentLifecycle('Dashboard');
  }, []);

  // Pulisci i messaggi dopo 5 secondi
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        onClearMessages();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage, onClearMessages]);

  const filteredData = useMemo(() => {
    const startTime = performance.now();
    
    const filtered = calculatedData.filter(row => {
      // Filtro ricerca ticker - CORRETTO: usa corrispondenza esatta invece di includes
      if (filters.searchTicker && row.Ticker.toLowerCase() !== filters.searchTicker.toLowerCase()) {
        return false;
      }

      // Filtro intervallo date
      if (filters.dateRange[0] && row.Date < filters.dateRange[0]) return false;
      if (filters.dateRange[1] && row.Date > filters.dateRange[1]) return false;

      // Filtro intervallo prezzo apertura
      if (row.Open < filters.openPriceRange[0] || row.Open > filters.openPriceRange[1]) return false;

      // Filtro intervallo Market Cap
      const marketCap = Number(row['Market Cap']) || 0;
      if (marketCap < filters.marketCapRange[0] || marketCap > filters.marketCapRange[1]) return false;

      // Filtro intervallo Shares Outstanding
      const sharesOutstanding = Number(row['Shares Outstanding']) || 0;
      if (sharesOutstanding < filters.sharesOutstandingRange[0] || sharesOutstanding > filters.sharesOutstandingRange[1]) return false;

      // Filtro intervallo Gap %
      const gapPercent = Math.abs(row['Gap %'] * 100);
      if (gapPercent < filters.gapPercentRange[0] || gapPercent > filters.gapPercentRange[1]) return false;

      // Filtro intervallo Volume Pre Market
      if (row['Pre Market Volume'] < filters.pmVolumeRange[0] || row['Pre Market Volume'] > filters.pmVolumeRange[1]) return false;

      // Filtro intervallo Open to PMH
      const openToPmhPercent = row['Open to PMH'] * 100;
      if (openToPmhPercent < filters.openToPmhRange[0] || openToPmhPercent > filters.openToPmhRange[1]) return false;

      // Filtro intervallo 5 Min Low %
      const fiveMinLowPercent = row['5 Min Low %'] * 100;
      if (fiveMinLowPercent < filters.fiveMinLowRange[0] || fiveMinLowPercent > filters.fiveMinLowRange[1]) return false;

      // Filtro intervallo 30 Min Low %
      const thirtyMinLowPercent = row['30 Min Low %'] * 100;
      if (thirtyMinLowPercent < filters.thirtyMinLowRange[0] || thirtyMinLowPercent > filters.thirtyMinLowRange[1]) return false;

      // Filtro intervallo orario HOD
      if (row['Hod Time']) {
        const hodTime = row['Hod Time'] instanceof Date 
          ? `${row['Hod Time'].getHours().toString().padStart(2, '0')}:${row['Hod Time'].getMinutes().toString().padStart(2, '0')}`
          : '00:00';
        
        if (hodTime < filters.hodTimeRange[0] || hodTime > filters.hodTimeRange[1]) return false;
      }

      // Filtro stato chiusura
      if (filters.closeStatus === 'red' && row['Red Close'] !== 1) return false;
      if (filters.closeStatus === 'green' && row['Red Close'] !== 0) return false;

      // Filtro Break PMH
      if (filters.pmhBreak === 'Y' && row['HIGH > PMH'] !== 'Y') return false;
      if (filters.pmhBreak === 'N' && row['HIGH > PMH'] !== 'N') return false;

      // Nuovo filtro Orario HOD
      if (filters.hodTimeFilter !== 'all' && row['Hod Time']) {
        const hodTime = row['Hod Time'] instanceof Date ? row['Hod Time'] : null;
        if (hodTime) {
          const hodHour = hodTime.getHours();
          const hodMinute = hodTime.getMinutes();
          const hodTotalMinutes = hodHour * 60 + hodMinute;
          const cutoffMinutes = 10 * 60 + 30; // 10:30 in minuti

          if (filters.hodTimeFilter === 'before_1030' && hodTotalMinutes >= cutoffMinutes) return false;
          if (filters.hodTimeFilter === 'after_1030' && hodTotalMinutes < cutoffMinutes) return false;
        }
      }

      return true;
    });
    
    const endTime = performance.now();
    console.log(`[Performance] Filtering ${calculatedData.length} records took ${(endTime - startTime).toFixed(2)}ms, resulting in ${filtered.length} records`);
    
    return filtered;
  }, [calculatedData, filters]);

  const kpis = useMemo(() => {
    const startTime = performance.now();
    const result = calculateKPIs(filteredData);
    const endTime = performance.now();
    
    console.log(`[Performance] KPI calculation for ${filteredData.length} records took ${(endTime - startTime).toFixed(2)}ms`);
    
    return result;
  }, [filteredData]);

  // Funzione per esportare i dati filtrati
  const handleExportData = () => {
    if (filteredData.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    // Crea CSV con i dati filtrati
    const headers = Object.keys(filteredData[0]);
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => 
        headers.map(header => {
          const value = (row as any)[header];
          if (value instanceof Date) {
            return value.toISOString();
          }
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');

    // Download del file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gap_analysis_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funzione per aggiornare i dati
  const handleRefreshData = () => {
    // Clear cache e ricarica
    clearDatabaseCache();
    window.location.reload();
  };
  // Track render time at the end of the component
  useEffect(() => {
    return () => {
      finishRenderTracking();
    };
  }, [finishRenderTracking]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
      {/* Messaggi di Stato */}
      {successMessage && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-900/20 border border-green-800 rounded-lg text-green-400 text-sm">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">THE EDGE DASHBOARD</h1>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <button
              onClick={() => setActiveTab('gaps')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'gaps'
                  ? 'bg-blue-400 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {filteredData.length} Gap (tutti i valori)
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'analytics'
                  ? 'bg-blue-400 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              Analisi e Grafici
            </button>
            <div className="text-xs sm:text-sm text-gray-400">
              Database: <span className="text-blue-400 font-medium">{currentDatabaseName}</span>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/journal')}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg transition-colors text-sm"
          >
            <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Journal & Position Sizing</span>
          </button>
          <button
            onClick={() => navigate('/dilution')}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white rounded-lg transition-colors text-sm"
          >
            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Analisi Diluizione</span>
          </button>
          <button
            onClick={() => navigate('/backtesting')}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-sm"
          >
            <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Backtester</span>
          </button>
          {/* Mostra il pulsante Gestisci DB solo agli admin - CORRETTO */}
          {isAdmin && (
            <button
              onClick={() => navigate('/manage-databases')}
              className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors text-sm"
            >
              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Gestisci DB</span>
            </button>
          )}
          <button
            onClick={onNewFile}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm"
          >
            <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Cambia Database</span>
          </button>

          {/* Menu Utente */}
          <UserMenu />
        </div>
      </div>

      {/* Contenuto Principale */}
      {activeTab === 'gaps' && (
        <DashboardLayout
          calculatedData={calculatedData}
          filteredData={filteredData}
          kpis={kpis}
          filters={filters}
          onFiltersChange={setFilters}
        />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsDashboard 
          data={calculatedData} 
        />
      )}
      
      {/* Performance Monitor (visible in development mode) */}
      <PerformanceMonitor visible={import.meta.env.DEV} />
    </div>
  );
}

function DashboardRouteContent({ 
  rawData, 
  currentDatabaseName,
  onDatabaseSelected,
  onNewFile,
  calculatedData,
  successMessage,
  errorMessage,
  onClearMessages
}: DashboardProps) {
  // Track component render time - MOVED BEFORE CONDITIONAL RETURN
  const finishRenderTracking = trackRenderTime('DashboardRouteContent');
  
  // Track component lifecycle - MOVED BEFORE CONDITIONAL RETURN
  useEffect(() => {
    return trackComponentLifecycle('DashboardRouteContent');
  }, []);

  // Track render time at the end of the component - MOVED BEFORE CONDITIONAL RETURN
  useEffect(() => {
    return () => {
      finishRenderTracking();
    };
  }, [finishRenderTracking]);

  // Se non c'è un database caricato, mostra la schermata di selezione
  if (!currentDatabaseName || rawData.length === 0) {
    return (
      <div className="flex-grow">
        <DatabaseSelection 
          onDatabaseSelected={onDatabaseSelected}
          onError={(error) => console.error(error)}
        />
      </div>
    );
  }

  return (
    <div className="flex-grow">
      <Dashboard 
        rawData={rawData}
        currentDatabaseName={currentDatabaseName}
        onDatabaseSelected={onDatabaseSelected}
        onNewFile={onNewFile}
        calculatedData={calculatedData}
        successMessage={successMessage}
        errorMessage={errorMessage}
        onClearMessages={onClearMessages}
      />
    </div>
  );
}

function App() {
  const [rawData, setRawData] = useState<StockData[]>([]);
  const [currentDatabaseName, setCurrentDatabaseName] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const { status: connectionStatus } = useConnectionStatus();
  const { retryConnection, isRetrying } = useConnectionStatus();
  const { networkStatus, resilientFetch } = useNetworkResilience();
  
  // Track component render time
  const finishRenderTracking = trackRenderTime('App');
  
  // Track component lifecycle
  useEffect(() => {
    return trackComponentLifecycle('App');
  }, []);

  // Memoizza i dati calcolati per migliorare le performance
  const calculatedData = useMemo(() => {
    if (rawData.length === 0) return [];
    
    const startTime = performance.now();
    const calculated = calculateMetrics(rawData);
    const endTime = performance.now();
    
    console.log(`[Performance] Calculated metrics for ${rawData.length} records in ${(endTime - startTime).toFixed(2)}ms`);
    
    return calculated;
  }, [rawData]);

  const handleDatabaseSelected = useCallback((data: StockData[], databaseName: string) => {
    console.log(`[App] Database selected: ${databaseName} with ${data.length} records`);
    setRawData(data);
    setCurrentDatabaseName(databaseName);
    setErrorMessage('');
    setSuccessMessage(`Database "${databaseName}" loaded successfully`);
  }, []);

  const handleNewFile = useCallback(() => {
    console.log('[App] Clearing current database');
    setRawData([]);
    setCurrentDatabaseName(null);
    setSuccessMessage('');
    setErrorMessage('');
    clearDatabaseCache();
  }, []);

  const handleSuccess = useCallback((message: string) => {
    console.log('[App] Success:', message);
    setSuccessMessage(message);
    setErrorMessage('');
  }, []);

  const handleError = useCallback((message: string) => {
    console.error('[App] Error:', message);
    setErrorMessage(message);
    setSuccessMessage('');
  }, []);

  const handleClearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const showGlobalLoading = useCallback((message: string) => {
    setIsGlobalLoading(true);
    setLoadingMessage(message);
    setShowTimeoutWarning(false);

    const timeoutId = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
      setIsGlobalLoading(false);
      setShowTimeoutWarning(false);
    };
  }, []);

  const hideGlobalLoading = useCallback(() => {
    setIsGlobalLoading(false);
    setLoadingMessage('');
    setShowTimeoutWarning(false);
  }, []);

  // Track render time at the end of the component
  useEffect(() => {
    return () => {
      finishRenderTracking();
    };
  }, [finishRenderTracking]);

  return (
    <ErrorBoundary>
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <Routes>
          <Route path="/" element={<div className="flex-grow"><LandingPage /></div>} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardRouteContent 
                  rawData={rawData}
                  currentDatabaseName={currentDatabaseName}
                  onDatabaseSelected={handleDatabaseSelected}
                  onNewFile={handleNewFile}
                  calculatedData={calculatedData}
                  successMessage={successMessage}
                  errorMessage={errorMessage}
                  onClearMessages={handleClearMessages}
                />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ticker/:tickerSymbol" 
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <TickerDetail data={calculatedData} />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/setup-occurrences"
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <SetupOccurrencesDetail calculatedData={calculatedData} />
                </div>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/manage-databases" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <div className="flex-grow">
                  <DatabaseManagement 
                    currentDatabaseName={currentDatabaseName}
                    onDatabaseSelected={handleDatabaseSelected}
                    onError={handleError}
                    onSuccess={handleSuccess}
                  />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/backtesting"
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div></div>}>
                    <Backtesting
                      calculatedData={calculatedData}
                      onDatabaseLoaded={handleDatabaseSelected}
                    />
                  </Suspense>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guided-analysis"
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div></div>}>
                    <GuidedAnalysis calculatedData={calculatedData} />
                  </Suspense>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dilution"
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div></div>}>
                    <DilutionAnalysis />
                  </Suspense>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal"
            element={
              <ProtectedRoute>
                <div className="flex-grow">
                  <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div></div>}>
                    <TradingJournal />
                  </Suspense>
                </div>
              </ProtectedRoute>
            }
          />
        </Routes>
        <Footer />
        
        {/* Performance Monitor (visible in development mode) */}
        <PerformanceMonitor visible={import.meta.env.DEV} />
        
        {/* Connection Status Indicator */}
        {connectionStatus !== 'connected' && (
          <div 
            className="fixed bottom-4 left-4 z-50 bg-red-900/80 text-white px-3 py-2 rounded-lg text-sm flex items-center space-x-2 cursor-pointer hover:bg-red-800/80 transition-colors"
            onClick={() => retryConnection()}
          >
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span>
              {connectionStatus === 'connecting' ? 'Connecting...' : 
               connectionStatus === 'timeout' ? 'Connection Timeout' : 
               'Connection Lost'}
            </span>
            {isRetrying && <span className="text-xs text-gray-300">(Retrying...)</span>}
          </div>
        )}

        {/* Global Loading Overlay */}
        <GlobalLoadingOverlay
          isLoading={isGlobalLoading}
          message={loadingMessage}
          showTimeout={showTimeoutWarning}
        />

        {/* Network Status Indicator */}
        <NetworkStatusIndicator />
      </div>
    </ErrorBoundary>
  );
}

export default App;